import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import useAdminLogin from './hooks/useAdminLogin';
import { lookSheets, useLookStyles, useOriginalPolish } from './screens/originalLook';

// Floodlight: the original black ground's blue glow rising from the bottom.
function BlueGlow() {
  if (Platform.OS !== 'web') return null;
  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { top: '50%', background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(30,80,220,0.18) 0%, transparent 70%)' }]}
    />
  );
}

export default function AdminLogin() {
  const { email, setEmail, password, setPassword, status, loading, showRestricted, onSignIn, goHome } = useAdminLogin();
  const styles = useLookStyles(SHEETS);
  const { isFloodlight, root } = useOriginalPolish();
  const fl = (props) => (isFloodlight ? props : {});
  const [focused, setFocused] = useState(null);
  const focusProps = (name) => fl({ onFocus: () => setFocused(name), onBlur: () => setFocused(null) });

  if (showRestricted) {
    return (
      <View style={styles.restrictedWrap} {...root}>
        {isFloodlight ? <BlueGlow /> : null}
        <Text style={styles.restrictedText} {...fl({ accessibilityRole: 'header' })}>Restricted. Admins only.</Text>
        <TouchableOpacity style={styles.homeBtn} onPress={goHome} {...fl({ accessibilityRole: 'button', accessibilityLabel: 'Go home' })}>
          <Text style={styles.homeBtnText}>Go Home</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container} {...root}>
      {isFloodlight ? <BlueGlow /> : null}
      <Text style={styles.title} {...fl({ accessibilityRole: 'header' })}>Admin Login</Text>
      <View style={styles.field}>
        <TextInput
          placeholder="Email"
          placeholderTextColor="rgba(255,255,255,0.5)"
          autoCapitalize="none"
          keyboardType="email-address"
          style={[styles.input, isFloodlight && focused === 'email' && styles.inputFocused]}
          value={email}
          onChangeText={setEmail}
          {...fl({ accessibilityLabel: 'Email', autoComplete: 'username', onSubmitEditing: () => { if (!loading) onSignIn(); } })}
          {...focusProps('email')}
        />
      </View>
      <View style={styles.field}>
        <TextInput
          placeholder="Password"
          placeholderTextColor="rgba(255,255,255,0.5)"
          style={[styles.input, isFloodlight && focused === 'password' && styles.inputFocused]}
          secureTextEntry
          value={password}
          onChangeText={setPassword}
          {...fl({ accessibilityLabel: 'Password', autoComplete: 'current-password', onSubmitEditing: () => { if (!loading) onSignIn(); } })}
          {...focusProps('password')}
        />
      </View>
      <TouchableOpacity style={styles.btn} onPress={onSignIn} {...fl({ accessibilityRole: 'button', accessibilityLabel: loading ? 'Signing in' : 'Sign in' })}>
        <Text style={styles.btnText}>{loading ? 'Signing in...' : 'Sign In'}</Text>
      </TouchableOpacity>
      {status ? <Text style={styles.status} {...fl({ accessibilityRole: 'alert' })}>{status}</Text> : null}
      <TouchableOpacity style={[styles.homeBtn, { marginTop: 12 }]} onPress={goHome} {...fl({ accessibilityRole: 'button', accessibilityLabel: 'Go home' })}>
        <Text style={styles.homeBtnText}>Go Home</Text>
      </TouchableOpacity>
    </View>
  );
}

const WEB = Platform.OS === 'web';
const ease = WEB ? { transition: 'border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease, transform 200ms ease' } : {};

// Floodlight-only refinements on top of the Archivo retype (Classic never sees these):
// the original column on the black ground, glass fields and the purple → blue accent.
const FL_OVERRIDES = {
  container: { backgroundColor: '#000000', overflow: 'hidden' },
  restrictedWrap: { backgroundColor: '#000000', overflow: 'hidden', padding: 24 },
  title: { fontSize: 24, fontWeight: '700', letterSpacing: -0.5, marginBottom: 20 },
  input: {
    height: 48,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    fontSize: 15,
    ...(WEB ? { outlineStyle: 'none', backdropFilter: 'blur(18px) saturate(140%)' } : {}),
    ...ease,
  },
  inputFocused: {
    borderColor: 'rgba(167,139,250,0.75)',
    backgroundColor: 'rgba(124,58,237,0.06)',
    ...(WEB ? { boxShadow: '0 0 0 3px rgba(124,58,237,0.22)' } : {}),
  },
  btn: {
    marginTop: 8,
    minHeight: 46,
    justifyContent: 'center',
    paddingHorizontal: 28,
    borderRadius: 12,
    backgroundColor: '#7C3AED',
    ...(WEB ? { backgroundImage: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 55%, #2563EB 100%)' } : {}),
    ...ease,
  },
  btnText: { color: '#FFFFFF', fontSize: 15, letterSpacing: 0.3 },
  status: { fontSize: 14, color: 'rgba(255,255,255,0.85)' },
  restrictedText: { fontSize: 18, fontWeight: '600', marginBottom: 16 },
  homeBtn: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    ...ease,
  },
  homeBtnText: { color: 'rgba(255,255,255,0.92)', fontSize: 14 },
};

const SHEETS = lookSheets({
  container: { flex: 1, backgroundColor: '#0b0b0b', alignItems: 'center', justifyContent: 'center', padding: 24 },
  title: { color: '#fff', fontSize: 20, fontWeight: '700', marginBottom: 16 },
  field: { width: '100%', maxWidth: 420, marginBottom: 10 },
  input: { height: 44, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', color: '#fff' },
  inputFocused: {},
  btn: { marginTop: 4, backgroundColor: '#fff', paddingVertical: 12, paddingHorizontal: 16, borderRadius: 8 },
  btnText: { color: '#000', fontWeight: '700' },
  status: { marginTop: 10, color: 'rgba(255,255,255,0.85)' },
  restrictedWrap: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#0b0b0b' },
  restrictedText: { color: '#fff', marginBottom: 12 },
  homeBtn: { backgroundColor: '#fff', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  homeBtnText: { color: '#000', fontWeight: '700' },
}, FL_OVERRIDES);
